import { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateTableFilterConfig } from '@/Poker/redux/slices/lobbySlice';
import useLobbySocket from './useLobbySocket';

// Filter configuration mapping
const FILTER_CONFIG = {
    '🔥 Turbo': { key: 'turbo', value: true },
    'RIT': { key: 'isRITEnabled', value: true },
    '⚡ Straddle': { key: 'isStraddleEnabled', value: true },
    'Deep': { key: 'isDeep', value: true },
    'Anonymous': { key: 'anonymous', value: true },
    'Happy Hours': { key: 'happyHoursEnabled', value: true },
    'Hide Full Tables': { key: 'hideFull', value: true, special: true },
    'Hide Empty Tables': { key: 'hideEmpty', value: true, special: true },
};

export default function useTableFilters() {
    const dispatch = useDispatch();
    const tableFilterConfig = useSelector(state => state.lobby.tableFilterConfig);
    const { saveTableFilters } = useLobbySocket();

    // Initialize local state from Redux
    const [appliedFilters, setAppliedFilters] = useState(new Set());

    // Sync local state with Redux when tableFilterConfig changes
    useEffect(() => {
        if (tableFilterConfig) {
            const activeFilters = new Set();
            
            // Map Redux state to filter labels
            if (tableFilterConfig.turbo) activeFilters.add('🔥 Turbo');
            if (tableFilterConfig.rit) activeFilters.add('RIT');
            if (tableFilterConfig.straddle) activeFilters.add('⚡ Straddle');
            if (tableFilterConfig.anonymous) activeFilters.add('Anonymous');
            if (tableFilterConfig.hideFull) activeFilters.add('Hide Full Tables');
            if (tableFilterConfig.hideEmpty) activeFilters.add('Hide Empty Tables');
            
            setAppliedFilters(activeFilters);
        }
    }, [tableFilterConfig]);

    // Toggle filter on/off
    const toggleFilter = (filterLabel) => {
        try {
            if (!filterLabel || typeof filterLabel !== 'string') {
                console.warn('Invalid filter label provided:', filterLabel);
                return;
            }

            setAppliedFilters(prev => {
                const newFilters = new Set(prev);
                if (newFilters.has(filterLabel)) {
                    newFilters.delete(filterLabel);
                } else {
                    newFilters.add(filterLabel);
                }

                // Update Redux state and save to backend with the new filters
                const newFilterConfig = {
                    turbo: newFilters.has('🔥 Turbo'),
                    rit: newFilters.has('RIT'),
                    straddle: newFilters.has('⚡ Straddle'),
                    anonymous: newFilters.has('Anonymous'),
                    hideFull: newFilters.has('Hide Full Tables'),
                    hideEmpty: newFilters.has('Hide Empty Tables'),
                };

                // Update Redux state
                dispatch(updateTableFilterConfig({ filters: newFilterConfig }));

                // Save to backend via lobby socket
                saveTableFilters(newFilterConfig);

                return newFilters;
            });
        } catch (error) {
            console.error('Error toggling filter:', error);
        }
    };

    // Remove a specific filter
    const removeFilter = (filterLabel) => {
        try {
            if (!filterLabel || typeof filterLabel !== 'string') {
                console.warn('Invalid filter label provided for removal:', filterLabel);
                return;
            }

            setAppliedFilters(prev => {
                const newFilters = new Set(prev);
                newFilters.delete(filterLabel);

                // Update Redux state and save to backend with the new filters
                const newFilterConfig = {
                    turbo: newFilters.has('🔥 Turbo'),
                    rit: newFilters.has('RIT'),
                    straddle: newFilters.has('⚡ Straddle'),
                    anonymous: newFilters.has('Anonymous'),
                    hideFull: newFilters.has('Hide Full Tables'),
                    hideEmpty: newFilters.has('Hide Empty Tables'),
                };

                // Update Redux state
                dispatch(updateTableFilterConfig({ filters: newFilterConfig }));

                // Save to backend via lobby socket
                saveTableFilters(newFilterConfig);

                return newFilters;
            });
        } catch (error) {
            console.error('Error removing filter:', error);
        }
    };

    // Clear all filters
    const clearAllFilters = () => {
        try {
            setAppliedFilters(new Set());

            // Update Redux state and save to backend
            const clearedFilterConfig = {
                turbo: false,
                rit: false,
                straddle: false,
                anonymous: false,
                hideFull: false,
                hideEmpty: false,
            };

            // Update Redux state
            dispatch(updateTableFilterConfig({ filters: clearedFilterConfig }));

            // Save to backend via lobby socket
            saveTableFilters(clearedFilterConfig);
        } catch (error) {
            console.error('Error clearing filters:', error);
        }
    };

    // Update Redux state with current filter configuration and save to backend
    const updateReduxFilters = () => {
        const newFilterConfig = {
            turbo: appliedFilters.has('🔥 Turbo'),
            rit: appliedFilters.has('RIT'),
            straddle: appliedFilters.has('⚡ Straddle'),
            anonymous: appliedFilters.has('Anonymous'),
            hideFull: appliedFilters.has('Hide Full Tables'),
            hideEmpty: appliedFilters.has('Hide Empty Tables'),
        };

        // Update Redux state
        dispatch(updateTableFilterConfig({ filters: newFilterConfig }));
        
        // Save to backend via lobby socket
        saveTableFilters(newFilterConfig);
    };

    // Filter table data based on applied filters and game definitions
    const filterTableData = (tableDataArray, gameDefinitionsMap) => {
        try {
            // Validate inputs
            if (!Array.isArray(tableDataArray)) {
                console.warn('Invalid tableDataArray provided:', tableDataArray);
                return [];
            }

            if (!gameDefinitionsMap || typeof gameDefinitionsMap !== 'object') {
                console.warn('Invalid gameDefinitionsMap provided:', gameDefinitionsMap);
                return tableDataArray; // Return original data if game definitions are invalid
            }

            if (appliedFilters.size === 0) {
                return tableDataArray;
            }

            return tableDataArray.filter(({ tableData }) => {
                try {
                    // Validate tableData
                    if (!tableData || typeof tableData !== 'object') {
                        console.warn('Invalid tableData in filter:', tableData);
                        return false;
                    }

                    // Validate gameDefId
                    if (!tableData.gameDefId) {
                        console.warn('Missing gameDefId in tableData:', tableData);
                        return false;
                    }

                    // Get game definition for this table
                    const gameDefinition = gameDefinitionsMap[tableData.gameDefId];

                    if (!gameDefinition) {
                        console.warn(`Game definition not found for gameDefId: ${tableData.gameDefId}`);
                        return false;
                    }

                    // Check if table matches ALL applied filters
                    return Array.from(appliedFilters).every(filterLabel => {
                        try {
                            const filterConfig = FILTER_CONFIG[filterLabel];
                            if (!filterConfig) {
                                console.warn(`Unknown filter label: ${filterLabel}`);
                                return true; // Unknown filter, don't filter out
                            }

                            // Handle special "Hide Full Tables" filter
                            if (filterConfig.special && filterConfig.key === 'hideFull') {
                                const seatedPlayerCount = tableData.seatedPlayerCount || 0;
                                const maximumPlayers = tableData.maximumPlayers || 0;
                                return seatedPlayerCount < maximumPlayers;
                            }
                            // Handle special "Hide Empty Tables" filter
                            if (filterConfig.special && filterConfig.key === 'hideEmpty') {
                                const seatedPlayerCount = tableData.seatedPlayerCount || 0;
                                const maximumPlayers = tableData.maximumPlayers || 0;
                                // Hide if table is empty (seatedPlayerCount === 0 and there are seats)
                                return !(seatedPlayerCount === 0 && maximumPlayers > 0);
                            }

                            const filterValue = gameDefinition[filterConfig.key];

                            // Handle undefined/null values gracefully
                            if (filterValue === undefined || filterValue === null) {
                                console.warn(`Filter property ${filterConfig.key} is undefined/null for gameDefId: ${tableData.gameDefId}`);
                                return false; // Filter out if property doesn't exist
                            }

                            return filterValue === filterConfig.value;
                        } catch (filterError) {
                            console.error(`Error processing filter ${filterLabel}:`, filterError);
                            return true; // Don't filter out on error
                        }
                    });
                } catch (tableError) {
                    console.error('Error processing table data:', tableError);
                    return false; // Filter out problematic tables
                }
            });
        } catch (error) {
            console.error('Error in filterTableData:', error);
            return tableDataArray; // Return original data on error
        }
    };

    // Get available filters with their configuration
    const getAvailableFilters = () => {
        try {
            return Object.keys(FILTER_CONFIG).map(label => ({
                label,
                config: FILTER_CONFIG[label],
                isApplied: appliedFilters.has(label)
            }));
        } catch (error) {
            console.error('Error getting available filters:', error);
            return [];
        }
    };

    // Validate if a filter label is valid
    const isValidFilter = (filterLabel) => {
        return FILTER_CONFIG.hasOwnProperty(filterLabel);
    };

    return {
        appliedFilters,
        toggleFilter,
        removeFilter,
        clearAllFilters,
        filterTableData,
        getAvailableFilters,
        isValidFilter,
        hasActiveFilters: appliedFilters.size > 0
    };
} 